/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { InputRendererHandler } from 'react-select';
import Select from 'react-select';

import classnames from 'classnames';
import $ from 'jquery';
import omit from 'lodash/omit';

import { FormValidationStatus, Grid, TextField, Typography2 } from '@coursera/cds-core';
import { ErrorIcon } from '@coursera/cds-icons';
import { color } from '@coursera/coursera-ui';
import { SvgChevronDown, SvgChevronUp } from '@coursera/coursera-ui/svg';

import type { BillingAddress, BillingAddressFieldName } from 'bundles/payments/common/types';
import validate from 'bundles/payments/lib/validate';
import { checkValidation, getLocationValidation } from 'bundles/payments/utils/formValidation';
import type { FieldDescriptor, MenuOption } from 'bundles/payments/utils/locationForm';
import { FULL_WIDTH, getLocationFields } from 'bundles/payments/utils/locationForm';

import _t from 'i18n!nls/payments';

import 'css!bundles/payments/components/inputs/__styles__/LocationFormGroup';
// eslint-disable-next-line import/extensions
import 'css!js/vendor/styl/coursera-ui.v0-1-4';

const { InvalidInputIcon } = validate;

const CA_POSTAL_CODE_DELIMITER = ' ';

export type Props = {
  address?: BillingAddress;
  onAddressChange?: (fieldName: string, value: string | null) => void;
  // TODO(fgoris) I have another diff partway implemented that fixes this and other TS issues.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onValidate?: ((e: React.SyntheticEvent<any> | React.KeyboardEvent<any>) => void) | null;
  defaultMonth?: string;
  disabled?: boolean;
  isFormSubmitted?: boolean;
  isTabLayout?: boolean;
  hideErrorIcon?: boolean;
  // TODO remove `shouldAlwaysTriggerChange` once all forms validate invalid inputs
  shouldAlwaysTriggerChange?: boolean;
  fieldClasses?: { [fieldName: string]: string };
  showCdsDesign?: boolean;
};

type State = {
  fieldAttempted: { [fieldName: string]: boolean };
  zipcode: string; // TODO remove once `shouldAlwaysTriggerChange` is removed
  postalCode: string;
};

const styles = {
  formContainer: css`
    .bt3-form-group {
      margin-bottom: 15px;
    }

    .bt3-has-success .bt3-radio {
      color: #3c763d;
    }

    .bt3-has-success .bt3-form-control {
      border-color: #3c763d;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%);
    }

    .bt3-has-success .bt3-form-control:focus {
      border-color: #366a37;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%), 0 0 6px #52a254;
    }

    .bt3-has-error .bt3-radio {
      color: #a94442;
    }

    .bt3-has-error .bt3-form-control {
      border-color: #a94442;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%);
    }

    .bt3-has-error .bt3-form-control:focus {
      border-color: #983d3b;
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 7.5%), 0 0 6px #c16361;
    }
  `,
  rowContainer: css`
    && {
      margin-bottom: 24px;
      font-family: 'Source Sans Pro', Arial, sans-serif !important;
    }
  `,
  textFieldInput: css`
    && {
      .cds-text-field-root {
        margin-top: unset;
      }

      .cds-input-input {
        padding: var(--cds-spacing-150) !important;
        background: inherit !important;
        border-color: #929599 !important;
      }
    }
  `,
  selectCountryContainer: css`
    && {
      .Select {
        padding: 0;

        &:focus,
        &:focus-within {
          &::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            z-index: 0;
            user-select: none;
            transform: scale(1, 1);
            box-shadow: 0 0 0 1px var(--cds-color-interactive-stroke-primary-focus),
              0 0 0 2px var(--cds-color-interactive-background-primary-hover-weak);
            border-radius: 4px;
          }

          .Select-control {
            border-color: var(--cds-color-neutral-primary-weak);
            background: var(--cds-color-neutral-background-primary);
          }
        }

        &:hover:not(.is-focused) {
          .Select-control {
            border-color: var(--cds-color-interactive-primary-hover);
            background: var(--cds-color-interactive-background-primary-hover-weak);
          }

          .Select-placeholder {
            color: var(--cds-color-neutral-primary) !important;
          }
        }

        .Select-placeholder {
          font-family: 'Source Sans Pro', Arial, sans-serif;
          color: #636363;
          display: flex;
          align-items: center;
        }

        .Select-menu-outer {
          z-index: 100;
          border: none;
          background-color: transparent;
        }

        .Select-menu {
          padding: 10px 10px;
          background: var(--cds-color-white-0);
          margin-top: var(--cds-spacing-150);
          border: 1px solid var(--cds-color-interactive-primary-hover);
          font-family: 'Source Sans Pro', Arial, sans-serif;
          border-radius: 4px;
        }

        .Select-control {
          border-radius: 4px !important;
          border: 1px solid #929599;
          font-size: 14px;
          padding: 11px !important;
        }

        .Select-input {
          width: 100% !important;
          height: auto;
          padding: 0;
          color: var(--cds-color-neutral-primary);

          input {
            padding: 0 !important;
            background: inherit !important;
          }
        }

        .Select-option {
          padding: var(--cds-spacing-100) !important;
          color: var(--cds-color-neutral-primary) !important;

          &.is-selected {
            color: var(--cds-color-interactive-primary-hover) !important;
          }

          &.is-focused {
            background: var(--cds-color-interactive-background-primary-hover-weak) !important;
            color: var(--cds-color-interactive-primary-hover) !important;
          }
        }
      }
    }
  `,

  selectCountryError: css`
    && {
      .Select {
        &:hover:not(.is-focused) {
          .Select-control {
            background: var(--cds-color-feedback-background-error-weak) !important;
          }
        }

        .Select-control {
          border-color: var(--cds-color-feedback-error-hover) !important;
        }

        .Select-menu {
          border-color: var(--cds-color-feedback-error-hover) !important;
        }
      }
    }
  `,
  selectCountrySuccess: css`
    && {
      .Select {
        &:hover:not(.is-focused) {
          .Select-control {
            background: var(--cds-color-feedback-background-success-weak) !important;
          }
        }

        .Select-control {
          border-color: var(--cds-color-feedback-success-hover) !important;
        }

        .Select-menu {
          border-color: var(--cds-color-feedback-success-hover) !important;
        }
      }
    }
  `,

  labelContainer: css`
    && {
      padding: 0;
      margin-bottom: 16px;
      text-transform: capitalize;
      display: flex;
      align-items: center;
    }
  `,
  validationError: css`
    background-color: transparent !important;
    padding-left: 0 !important;
  `,
};

class LocationFormGroup extends React.Component<Props, State> {
  static defaultProps = {
    fieldClasses: {
      country: '',
      zipcode: '',
      postalCode: '',
    },
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      fieldAttempted: {},
      zipcode: ((props || {}).address || {}).zipcode || '',
      postalCode: ((props || {}).address || {}).postalCode || '',
    };
  }

  onSearchableSelectChange = ({ name, value }: { name: string; value?: string }) => {
    if (this.wrapperRefs[name]) {
      const el = $(this.wrapperRefs[name]).find('input')[0];
      // TODO(fgoris) I have another diff partway implemented that fixes this and other TS issues.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      this.onFieldChange({ target: { id: el.id, name, value } });
    }
  };

  // TODO(fgoris) I have another diff partway implemented that fixes this and other TS issues.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFieldChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    const { address } = this.props;

    let formattedValue = value;
    if (name === 'postalCode' && address?.country === 'CA') {
      formattedValue = formattedValue.split(CA_POSTAL_CODE_DELIMITER).join('').toUpperCase();
      if (formattedValue.length > 0) {
        formattedValue = formattedValue.match(new RegExp('.{1,3}', 'g')).join(CA_POSTAL_CODE_DELIMITER);
      }
    }

    const { onAddressChange, onValidate } = this.props;

    if (onAddressChange) {
      onAddressChange(name, formattedValue);

      // Resetting validity of postalCode and zipcode fields here because these fields only show conditionally when country changes
      if (name === 'country') {
        onAddressChange('zipcode', null);
        onAddressChange('postalCode', null);

        this.setState(() => ({
          zipcode: '',
          postalCode: '',
        }));
      }
    }

    // `onValidate` is used to do something with the event that triggered the field change
    // e.g. on B2C checkout, auto tab to the next field
    if (this.isFieldValid(name, formattedValue) && onValidate) {
      // TODO Instead of doing this hack, refactor focusOnNextField to work with onChange events.
      // focusOnNextField expects a keyboard event, i.e. with e.key
      // TODO(fgoris) I have another diff partway implemented that fixes this and other TS issues.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      onValidate({ target: e.target, key: formattedValue[formattedValue.length - 1] });
    }

    this.setState(({ fieldAttempted, zipcode, postalCode }) => ({
      fieldAttempted: {
        ...fieldAttempted,
        [name]: true,
      },
      // TODO remove once `shouldAlwaysTriggerChange` is removed
      zipcode: name === 'zipcode' ? formattedValue : zipcode,
      postalCode: name === 'postalCode' ? formattedValue : postalCode,
    }));
  };

  // TODO remove this method once `shouldAlwaysTriggerChange` is removed
  onBlur = () => {
    const { zipcode, postalCode } = this.state;
    const { onAddressChange, shouldAlwaysTriggerChange } = this.props;

    if (onAddressChange && !shouldAlwaysTriggerChange) {
      if (zipcode && !this.isFieldValid('zipcode', zipcode)) {
        onAddressChange('zipcode', null);
      }

      if (postalCode && !this.isFieldValid('postalCode', postalCode)) {
        onAddressChange('postalCode', null);
      }
    }
  };

  wrapperRefs: { [name: string]: HTMLDivElement } = {};

  isFieldValid = (fieldName: BillingAddressFieldName, value: string): boolean => {
    const { address } = this.props;
    const { validator, required } = getLocationValidation(address)[fieldName] || {};
    const isNotRequiredAndEmpty = !required && !value;
    const isRequiredAndFilled = !!validator && validator(value);
    return isNotRequiredAndEmpty || isRequiredAndFilled;
  };

  renderArrow({ isOpen }: { isOpen: boolean }) {
    const props = {
      size: 24,
      style: { verticalAlign: 'bottom' },
      color: color.primary,
    };

    return isOpen ? <SvgChevronUp {...props} /> : <SvgChevronDown {...props} />;
  }

  renderAutocompleteInput: InputRendererHandler = (props) => {
    // react-select v1's assignment of aria props is causing VoiceOver to incorrectly announce the combobox; this manually fixes it
    // TODO: remove once web is migrated to React 16.x and react-select v2
    return (
      <div
        role="combobox"
        aria-controls={props['aria-owns']}
        aria-haspopup="listbox"
        aria-owns={props['aria-owns']}
        aria-expanded={props['aria-expanded']}
        className="Select-input"
        aria-describedby={props['aria-describedby']}
        aria-invalid={props['aria-invalid']}
      >
        <input
          type="text"
          {...omit(props, ['role', 'aria-owns', 'aria-haspopup', 'aria-expanded'])}
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls={props['aria-owns']}
          aria-describedby={props['aria-describedby']}
          aria-invalid={props['aria-invalid']}
        />
      </div>
    );
  };

  // react-select currently has span "Select-value-label" with role="option" which violates aria-required-children
  // to fix a11y, we will render our own value component without the role
  renderValueComponent = (option?: MenuOption, idName?: string) => {
    if (!option) return undefined;
    return () => (
      <div className="Select-value">
        <span className="Select-value-label" id={`react-select-${idName}--value-item`}>
          {option.label}
        </span>
      </div>
    );
  };

  getValidationErrorMessage = (fieldName: BillingAddressFieldName) => {
    const { address } = this.props;
    const locationValidation = getLocationValidation(address) || {};
    const validation = locationValidation[fieldName];
    const value = address?.[fieldName]?.toString();
    const { message } = checkValidation(validation, value, fieldName);
    return message;
  };

  renderFormField = (fieldDescriptor: FieldDescriptor) => {
    const { name, placeholder, size = FULL_WIDTH } = fieldDescriptor;
    const {
      address = {},
      isFormSubmitted,
      disabled,
      isTabLayout,
      hideErrorIcon,
      fieldClasses = {},
      showCdsDesign,
    } = this.props;

    const { label = '', required } = getLocationValidation(address)[name] || {};
    const { fieldAttempted, zipcode: internalZipcode, postalCode: internalPostalCode } = this.state;
    const fieldClass = fieldClasses[name];
    const value =
      address[name] || (name === 'zipcode' ? internalZipcode : '') || (name === 'postalCode' ? internalPostalCode : '');
    const isValid = this.isFieldValid(name, String(value));
    const isAttempted: boolean = fieldAttempted[name];

    const hasError = (isAttempted || !!isFormSubmitted) && !isValid;
    const hasSuccess = (isAttempted || isFormSubmitted) && isValid;
    const formGroupClasses = classnames({
      'bt3-form-group': !isTabLayout,
      'bt3-has-success': (isAttempted || isFormSubmitted) && isValid,
      'bt3-has-error': hasError,
      [`bt3-col-xs-${size.mobile} bt3-col-sm-${size.desktop} no-margin no-gutters`]: isTabLayout && !fieldClass,
      [fieldClass]: !!fieldClass,
    });

    const getValidationStatus = () => {
      if (hasSuccess) {
        return 'success';
      } else if (hasError) {
        return 'error';
      }

      return undefined;
    };
    let formField: JSX.Element | null = null;
    // FieldDescriptor, by definition, has only 2 possible values of `type`.
    // It's either this, or suppress `TS2339: Property 'type' does not exist on type 'never'.` in the `default` case.
    // eslint-disable-next-line default-case
    switch (fieldDescriptor.type) {
      case 'text': {
        const { pattern, maxLength } = fieldDescriptor;
        formField = showCdsDesign ? (
          <TextField
            type="text"
            value={value}
            onBlur={['zipcode', 'postalCode'].includes(name) ? this.onBlur : undefined}
            disabled={disabled}
            id={`cc-${name}`}
            aria-label={name}
            name={name}
            maxLength={maxLength}
            data-e2e={name}
            inputProps={{
              pattern,
              'aria-required': required,
              'aria-invalid': hasError,
              'aria-describedby': `validation-status-${name}`,
            }}
            onChange={this.onFieldChange}
            placeholder={placeholder}
            validationStatus={getValidationStatus()}
            fullWidth
            css={styles.textFieldInput}
          />
        ) : (
          <input
            className={classnames('location-input larger-font-size', {
              'padded-input': isTabLayout,
            })}
            type="text"
            value={value}
            pattern={pattern}
            autoComplete="off"
            placeholder={placeholder}
            maxLength={maxLength}
            onChange={this.onFieldChange}
            onBlur={['zipcode', 'postalCode'].includes(name) ? this.onBlur : undefined}
            name={name}
            data-e2e={name}
            disabled={disabled}
            id={`cc-${name}`}
            aria-invalid={hasError}
            aria-required={required}
          />
        );
        break;
      }
      case 'menu': {
        const { options } = fieldDescriptor;
        if (isTabLayout) {
          const selectedOption = options.find((opt) => opt.value === value);
          formField = (
            <div
              css={
                showCdsDesign
                  ? [
                      styles.selectCountryContainer,
                      hasError && styles.selectCountryError,
                      hasSuccess && styles.selectCountrySuccess,
                    ]
                  : ''
              }
            >
              <Select
                className={!showCdsDesign ? 'larger-font-size' : ''}
                placeholder={showCdsDesign ? _t('Country') : placeholder}
                id={`cc-${name}`}
                inputProps={{
                  'data-e2e': name,
                  id: `cc-${name}`,
                  'aria-describedby': `validation-status-${name}`,
                  'aria-invalid': hasError,
                }}
                onChange={(opt) => this.onSearchableSelectChange({ ...opt, name })}
                value={value}
                // @ts-expect-error TODO: options labels can only be strings, but we are trying to pass FormattedMessage here
                options={options}
                disabled={disabled}
                arrowRenderer={this.renderArrow}
                inputRenderer={this.renderAutocompleteInput}
                valueComponent={this.renderValueComponent(selectedOption, `cc-${name}`)}
                optionHeight={48}
                searchable={true}
                clearable={false}
                instanceId={`cc-${name}`}
                required={required}
                aria-describedby={`validation-status-${name}`}
              />
            </div>
          );
        } else {
          const optionsElements = options.map((option: MenuOption) => {
            return (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            );
          });
          formField = (
            <select
              name={name}
              value={value}
              className={classnames('location-select larger-font-size', {
                'default-color': !isAttempted && !isFormSubmitted,
                'padded-select': isTabLayout,
              })}
              onChange={this.onFieldChange}
              disabled={disabled}
              id={`cc-${name}`}
              required={required}
            >
              <option className="label-text" value="" disabled={!!isAttempted}>
                {placeholder}
              </option>
              {optionsElements}
            </select>
          );
        }
        break;
      }
    }

    let content: JSX.Element | null;
    const validationError = this.getValidationErrorMessage(name);
    if (showCdsDesign) {
      content = (
        <Grid
          item
          key={name}
          ref={(c) => {
            if (c) {
              this.wrapperRefs[name] = c;
            }
          }}
          xs={size.mobile}
          lg={size.desktop}
        >
          <label css={styles.labelContainer} htmlFor={`cc-${name}`}>
            <Typography2 component="div" variant="subtitleMedium">
              {label}
            </Typography2>
            {!hideErrorIcon && name === 'country' && hasError && <ErrorIcon color="error" size="small" />}
          </label>
          {formField}
          <div aria-live="assertive">
            {hasError && validationError && (
              <FormValidationStatus
                label={validationError}
                id={`validation-status-${name}`}
                css={styles.validationError}
                validationStatus="error"
                hideIcon={true}
              />
            )}
          </div>
        </Grid>
      );
    } else if (isTabLayout) {
      content = (
        <div
          key={name}
          className={formGroupClasses}
          ref={(c) => {
            if (c) {
              this.wrapperRefs[name] = c;
            }
          }}
        >
          <label className="label-text" htmlFor={`cc-${name}`}>
            {label}
            {!hideErrorIcon && <InvalidInputIcon hasError={hasError} />}
          </label>
          {formField}
        </div>
      );
    } else {
      content = (
        <div className={formGroupClasses} key={name}>
          <div className="bt3-col-sm-1" />
          <label className="bt3-col-sm-3" htmlFor={`cc-${name}`}>
            <b className="body-2-text">{label}</b>
            {!hideErrorIcon && <InvalidInputIcon hasError={hasError} />}
          </label>
          <div className="bt3-col-sm-7">{formField}</div>
        </div>
      );
    }

    return content;
  };

  createFormRows(formFields: FieldDescriptor[]): Array<FieldDescriptor[]> {
    return formFields.reduce(
      (rows: Array<FieldDescriptor[]>, field: FieldDescriptor) => {
        let lastRow = rows[rows.length - 1];
        const lastRowDesktopWidth: number = lastRow.reduce(
          (rowWidth: number, lastRowField: FieldDescriptor) => rowWidth + (lastRowField.size || FULL_WIDTH).desktop,
          0
        );
        if (lastRowDesktopWidth + (field.size || FULL_WIDTH).desktop > 12) {
          lastRow = [];
          rows.push(lastRow);
        }
        lastRow.push(field);
        return rows;
      },
      [[]]
    );
  }

  render() {
    const { address = {}, isTabLayout, showCdsDesign } = this.props;
    const { country } = address;

    const formFields = getLocationFields({
      country,
    });

    let content;
    if (showCdsDesign) {
      const formRows = this.createFormRows(formFields);
      content = formRows.map((rowFields: FieldDescriptor[]) => (
        <Grid container key={rowFields[0].name} spacing={16} css={styles.rowContainer}>
          {rowFields.map((field: FieldDescriptor) => this.renderFormField(field))}
        </Grid>
      ));
    } else if (isTabLayout) {
      const formRows = this.createFormRows(formFields);
      content = formRows.map((rowFields: FieldDescriptor[]) => (
        <div className="bt3-row no-margin" key={rowFields[0].name}>
          {rowFields.map((field: FieldDescriptor) => this.renderFormField(field))}
        </div>
      ));
    } else {
      content = <div>{formFields.map((field: FieldDescriptor) => this.renderFormField(field))}</div>;
    }

    return (
      <div css={styles.formContainer} className="rc-LocationFormGroup">
        {content}
      </div>
    );
  }
}

export default LocationFormGroup;
